import { type InventoryView, type ItemAffix } from "../types";

export const STANDARD_NUMBER_FORMAT = new Intl.NumberFormat("zh-CN", {
  useGrouping: false,
  maximumFractionDigits: 1,
});

export const PRECISE_NUMBER_FORMAT = new Intl.NumberFormat("zh-CN", {
  useGrouping: false,
  maximumFractionDigits: 4,
});

export function collectCombatBonuses(
  stats: InventoryView["stats"],
): Array<{ label: string; value: string }> {
  const values: Array<[string, number]> = [
    ["护甲穿透", stats.armorPenetrationPercent],
    ["法力获取", stats.manaGainPercent],
    ["钥匙获取", stats.keyGainPercent],
    ["木材产量", stats.woodYieldPercent],
    ["矿石产量", stats.oreYieldPercent],
    ["格挡上限", stats.blockCapPercent],
    ["规模系数", stats.scaleBonusPercent],
    ["连锁系数", stats.cascadeBonusPercent],
    ["治疗效果", stats.healingBonusPercent],
    ["暴击率", stats.criticalChance],
    ["暴击伤害", stats.criticalDamagePercent],
    ["主动技能冷却缩减", stats.skillCooldownReductionPercent],
    ["自动三消速度", stats.automaticSpeedPercent],
  ];
  return values
    .filter(([, value]) => Math.abs(value) > 0.000001)
    .map(([label, value]) => ({ label, value: formatPercentBonus(value) }));
}

export function formatPercentBonus(value: number): string {
  const percent = value * 100;
  return `${percent > 0 ? "+" : ""}${formatDisplayNumber(percent)}%`;
}

export function formatEnhancementValue(value: number): string {
  return formatDisplayNumber(value);
}

export function formatDisplayNumber(value: number, highPrecision = false): string {
  if (!Number.isFinite(value)) return "0";
  const digits = highPrecision ? 4 : 1;
  const normalized = Math.abs(value) < 0.5 * 10 ** -digits ? 0 : value;
  return (highPrecision ? PRECISE_NUMBER_FORMAT : STANDARD_NUMBER_FORMAT).format(normalized);
}

export function formatRecoveryDuration(milliseconds: number): string {
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${totalMinutes} 分钟`;
  return `${hours} 小时${minutes > 0 ? ` ${minutes} 分钟` : ""}`;
}

export function formatOfflineDuration(seconds: number): string {
  const minutes = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${minutes} 分`;
  return remainder === 0 ? `${hours} 小时` : `${hours} 小时 ${remainder} 分`;
}

export function formatTaskReset(resetAt: number): string {
  const date = new Date(resetAt);
  const weekday = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][date.getDay()];
  return `${date.getMonth() + 1}月${date.getDate()}日 ${weekday} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
}

export function formatAffixAmount(mode: string, value: number, highPrecision = false) {
  return `+${mode === "percent" ? `${formatDisplayNumber(value * 100, highPrecision)}%` : formatDisplayNumber(value, highPrecision)}`;
}

export function rollGrade(roll = 5000) {
  return roll >= 8500 ? "S" : roll >= 7000 ? "A" : roll >= 5000 ? "B" : roll >= 2500 ? "C" : "D";
}

export function formatAffixResult(affix: ItemAffix) {
  return affix.mode === "mechanic"
    ? affix.name
    : `${affix.name} ${formatAffixAmount(affix.mode, affix.value, true)}`;
}

export function currencyLabel(currency: string): string {
  return currency === "wood"
    ? "木"
    : currency === "ore"
      ? "矿"
      : currency === "gold"
        ? "金"
        : currency === "diamond"
          ? "钻石"
          : "结晶";
}

export function formatRunDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${clock}` : clock;
}

export function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "发生未知错误";
}

export function formatTraitValue(value: number): string {
  return formatDisplayNumber(value, true);
}
